import React, { useEffect, useId, useRef } from "react";
import "./PortfolioModal.css";

function getGifForCompany(companyName = "") {
  const company = String(companyName).toLowerCase();
  if (company.includes("ups")) return "images/ups.webp";
  if (company.includes("costar")) return "images/costar.gif";
  if (company.includes("chick")) return "images/cfa.gif";
  if (company.includes("cargill")) return "images/cargill.gif";
  if (company.includes("bmw")) return "images/bmw.gif";
  if (company.includes("upwork") || company.includes("freelance")) return "images/ruby.gif";
  return null;
}

export default function PortfolioModal({ isOpen, onRequestClose, content = {} }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    previousFocusRef.current = document.activeElement;
    const focusFrame = window.requestAnimationFrame(() => closeRef.current?.focus());

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onRequestClose?.();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute("hidden"));

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen, onRequestClose]);

  if (!isOpen) return null;

  const gif = getGifForCompany(content.company_name);
  const gifSrc = gif ? `${process.env.PUBLIC_URL}/${gif}` : null;
  const logoSrc = content.imageUrl ? `${process.env.PUBLIC_URL}/${content.imageUrl}` : null;
  const technologies = Array.isArray(content.tech_stack) ? content.tech_stack : [];

  return (
    <div
      className="portfolio-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onRequestClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="portfolio-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="modal-header">
          <button ref={closeRef} type="button" className="modal-close" onClick={onRequestClose} aria-label="Close">
            Close
          </button>

          {gifSrc && (
            <img
              className="modal-company-gif"
              src={gifSrc}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          )}

          <div className="modal-title-area">
            <h2 id={titleId} className="modal-title">{content.title || "Experience details"}</h2>
            <div className="modal-subtitle">
              {content.company_name && <span className="modal-company">{content.company_name}</span>}
              {content.date && <span className="modal-date">{content.date}</span>}
            </div>

            {logoSrc && (
              <img
                className="modal-company-logo"
                src={logoSrc}
                alt={`${content.company_name || "Company"} logo`}
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}
          </div>
        </div>

        {technologies.length > 0 && (
          <div className="modal-tech" aria-label="Technologies">
            {technologies.map((technology) => (
              <span key={technology} className="tech-chip">{technology}</span>
            ))}
          </div>
        )}

        <div className="modal-body">
          <p id={descriptionId} className="modal-description">{content.description}</p>
        </div>
      </div>
    </div>
  );
}
